import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import {
  DonateCheckbox,
  CancelAndReturnUrl,
  LogoImage,
  CampaignImage,
  SingleAmount,
} from '@paypalcorp/donate-react';
import { MessageFormat } from '@paypalcorp/worldready';
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react';
import withTheme from '../../componentsV3/withTheme';
import PresetAmountPreviewComponent from '../../shared/PresetAmountPreviewComponent';
import {
  Button,
  BodyText,
  IconButton,
  CloseIcon,
  HeadingText,
} from '@paypalcorp/pp-react';
import { appendCdnHostName } from '../../../utility/geoUtils';
import { displayCurrency, normalizeNumber } from '../../utilsV3/amountUtils';
import { formatCurrencyToParts } from '../../../utility/worldReadyUtils';
import PPCC from '../../shared/ppcc';

const PaymentCardPreview = ({
  isMobile = false,
  isMobilePreviewOpen = false,
}) => {
  const worldReady = useContext(WorldReadyContext);

  const otherAmount = new MessageFormat(worldReady, {
    id: 'buttonsV3.buttonOverview.phonePreview.otherAmount',
  }).format();

  const campaignBannerImageHolderText = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.campaignImage.holderText',
  }).format();

  const campaignLogoImageHolderText = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.logoImage.holderText',
  }).format();

  const html = document.getElementsByTagName('html')[0];
  const directionality = html.getAttribute('dir');
  const formValues = useSelector((state) => {
    return (
      state.form?.campaigns?.values ??
      state?.buttonManagerV3?.formattedCampaign ??
      {}
    );
  });
  const receiverName = formValues?.receiverName;
  const noCancelReturn = formValues?.noCancelReturn;
  const cancelUrl = formValues?.cancelReturn;
  const showCancelOrReturn = noCancelReturn === 'no' && !_isEmpty(cancelUrl);
  const contentCancelAndReturn = (
    <Message
      id="setup.campaigns.campaignPreview.campaignDesktopPreview.cancelAndReturn"
      orgName={receiverName}
    />
  );
  const bannerImageUrl = formValues.bannerImageUrl;

  const {
    amountType,
    amountValue,
    currencyCode,
    merchantAmount1,
    merchantAmount2,
    merchantAmount3,
    coverFeesEnabled,
    logoUrl,
    businessProfileLogoUrl,
    noLogoImage,
    recurringEnabled,
    hasEndTime,
    status,
    targetAmount,
    TotalAmount,
  } = formValues;
  const isPresetAmount = amountType === 'merchant_recommended';
  const amountOptions = [
    ...(isPresetAmount
      ? [merchantAmount1, merchantAmount2, merchantAmount3]
      : []),
  ];
  const currencyParts = formatCurrencyToParts(0, { currency: currencyCode });
  const currencySymbol = currencyParts?.find(
    (cp) => cp.type === 'symbol'
  )?.value;
  let singleAmountValue = normalizeNumber(amountValue);
  singleAmountValue = !isNaN(singleAmountValue)
    ? singleAmountValue.toString()
    : '';

  const renderAmounts = () => {
    if (!isPresetAmount) {
      return (
        <SingleAmount
          amount={singleAmountValue}
          currencyCode={currencyCode}
          isEditable={false}
          isPreview={true}
          currencySymbol={currencySymbol}
          directionality={directionality}
        />
      );
    }
    return (
      <PresetAmountPreviewComponent
        amountOptions={amountOptions}
        currencyCode={currencyCode}
        otherAmountLabel={otherAmount}
      />
    );
  };

  const renderCoverFees = () =>
    coverFeesEnabled === 'yes' && (
      <DonateCheckbox
        className="cover-fee-checkbox"
        id="cover-fee-checkbox"
        data-testid="cover-fee-checkbox"
        name="coverFee"
        checkbox_content={
          <Message
            id="common.coverFee.wr.labelA"
            amount={
              <strong id="fee-amount">
                {displayCurrency(0, currencyCode)}
              </strong>
            }
          />
        }
        disabled={true}
      />
    );

  const renderRecurring = () =>
    recurringEnabled === 'yes' &&
    hasEndTime === 'no' && (
      <DonateCheckbox
        className="recurring-donation-checkbox"
        id="recurring-checkbox"
        checkbox_content={new MessageFormat(worldReady, {
          id: 'setup.campaigns.campaignPreview.campaignCheckbox.recurringCheckbox',
        }).format()}
        disabled={true}
      />
    );

  const renderPaymentOptions = () => {
    if (status === 'ACTIVE' || status === 'DRAFT')
      return (
        <>
          {renderAmounts()}
          {renderCoverFees()}
          {renderRecurring()}
          <Button branded className="next-button" tabIndex={-1}>
            <img
              src={appendCdnHostName(
                '/paypal-ui/logos/svg/paypal-mark-color.svg'
              )}
              style={{ height: '24px', width: '24px', marginRight: '8px' }}
            />
            <Message id="buttonsV3.buttonOverview.phonePreview.donateWithPayPal" />
          </Button>
          <Button
            className={isMobile ? 'btn-card' : ''}
            secondary
            disabled={true}
            tabIndex={-1}
          >
            <Message id="buttonsV3.buttonOverview.phonePreview.donateWithCard" />
          </Button>
        </>
      );
    // Campaign closed & goal reached
    if (status === 'INACTIVE' && TotalAmount >= targetAmount) {
      return (
        <HeadingText size="sm">
          <Message id="setup.campaigns.campaignPreview.preview.showSupport" />
        </HeadingText>
      );
    }
    // Campaign closed & any amount raised
    if (status === 'INACTIVE') {
      return (
        <BodyText>
          <Message id="setup.campaigns.campaignPreview.preview.noDonationText" />
        </BodyText>
      );
    }
  };

  return (
    <div
      className={
        isMobile ? 'campaign-common-preview-container' : 'campaign-payment-card'
      }
    >
      {isMobile && !isMobilePreviewOpen && (
        <CampaignImage
          data-testid="campaignBannerImage"
          src={bannerImageUrl}
          content={{
            holderText: campaignBannerImageHolderText,
          }}
        />
      )}

      <div className="wrapper-paymentCard">
        <div className="paymentCard">
          {isMobile && !isMobilePreviewOpen && (
            <div className="title-preview">
              <BodyText>
                <Message id="setup.campaigns.campaignPreview.preview.donateButton" />
              </BodyText>
              <IconButton
                svgIconComponent={CloseIcon}
                className="close-button"
                buttonType="tertiary"
              />
            </div>
          )}
          {noLogoImage === 'no' && (
            <LogoImage
              src={logoUrl || businessProfileLogoUrl}
              content={{
                holderText: campaignLogoImageHolderText,
              }}
              displayIcon={false}
              style={{
                marginTop: 24,
                marginBottom: 24,
              }}
            />
          )}
          <BodyText
            className="org-name"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span>{receiverName}</span>
            <span className="preview-verified-account-icon">
              <PPCC />
            </span>
          </BodyText>
          {renderPaymentOptions()}
        </div>
        <div className="cancel-return">
          {showCancelOrReturn && (
            <CancelAndReturnUrl content={contentCancelAndReturn} />
          )}
        </div>
      </div>
    </div>
  );
};

PaymentCardPreview.propTypes = {
  isMobile: PropTypes.bool,
  isMobilePreviewOpen: PropTypes.bool,
};

export default withTheme(PaymentCardPreview);
